import { BedrockRuntimeClient, ConverseCommand, InvokeModelCommand, ApplyGuardrailCommand, ToolInputSchema } from '@aws-sdk/client-bedrock-runtime';
import type { GuardrailAssessment } from '@aws-sdk/client-bedrock-runtime';
import { CognitoService } from 'src/services/aws/CognitoService';
import { SSMService } from 'src/services/aws/SSMService';
import { logger } from 'src/logger';

/**
 * A JSON Schema document, as the Converse API accepts it. Derived from the SDK's own
 * type rather than imported from `@smithy/types`, which is only a transitive
 * dependency here.
 */
type JsonSchemaDocument = ToolInputSchema.JsonMember['json'];

/**
 * Thrown when a guardrail blocks the request. Callers should branch on this type rather
 * than matching the message, which is the guardrail's own configured copy and carries no
 * guaranteed wording.
 *
 * `source` distinguishes the cause: `INPUT` means the prompt or section data tripped a
 * filter (commonly PROMPT_ATTACK on directive phrasing); `OUTPUT` means the model's
 * response did.
 */
export class GuardrailBlockedError extends Error {
  public readonly source: 'INPUT' | 'OUTPUT';

  constructor(source: 'INPUT' | 'OUTPUT', message: string) {
    super(message);
    this.name = 'GuardrailBlockedError';
    this.source = source;
    // Required for `instanceof` to work when compiled down to ES5.
    Object.setPrototypeOf(this, GuardrailBlockedError.prototype);
  }
}

const BEDROCK_REGION = 'us-west-2';
const MODEL_ID = 'us.anthropic.claude-sonnet-4-5-20250929-v1:0';
/** Default output ceiling. Callers with a known-short response should pass a lower value. */
const DEFAULT_MAX_TOKENS = 4096;
const GUARDRAIL_ID_PARAM = '/das-finsuite/guardrail/EAHuddleGuardrail/id';
const GUARDRAIL_VERSION_PARAM = '/das-finsuite/guardrail/EAHuddleGuardrail/version';

/**
 * Every policy section that reported on an intervention, as `<policy>:<identifier>` pairs across all
 * assessments. Confidence, filter strength and grounding scores are excluded because they are
 * per-attempt tuning feedback for whoever is probing the surface; `match` and `regex` are excluded
 * because they carry the offending content. A custom word's only identifier IS its match, so those
 * collapse to a constant.
 */
const interventionPolicies = (assessments: GuardrailAssessment[] = []): string[] => [
  ...new Set(
    assessments.flatMap((assessment) => [
      ...(assessment.topicPolicy?.topics ?? []).map((topic) => `topicPolicy:${topic.name}`),
      ...(assessment.contentPolicy?.filters ?? []).map((filter) => `contentPolicy:${filter.type}`),
      ...(assessment.wordPolicy?.customWords ?? []).map(() => 'wordPolicy:CUSTOM_WORD'),
      ...(assessment.wordPolicy?.managedWordLists ?? []).map((word) => `wordPolicy:${word.type}`),
      ...(assessment.sensitiveInformationPolicy?.piiEntities ?? []).map((entity) => `sensitiveInformationPolicy:${entity.type}`),
      ...(assessment.sensitiveInformationPolicy?.regexes ?? []).map((regex) => `sensitiveInformationPolicy:${regex.name ?? 'REGEX'}`),
      ...(assessment.contextualGroundingPolicy?.filters ?? []).map((filter) => `contextualGroundingPolicy:${filter.type}`)
    ])
  )
];

export class BedrockService {
  private static client: BedrockRuntimeClient | null = null;
  private static guardrailId: string | null = null;
  private static guardrailVersion: string | null = null;

  private static getClient(): BedrockRuntimeClient {
    if (this.client) return this.client;
    this.client = new BedrockRuntimeClient({
      region: BEDROCK_REGION,
      credentials: CognitoService.credentialProvider
    });
    return this.client;
  }

  private static async loadGuardrailConfig(): Promise<{ guardrailId: string; guardrailVersion: string }> {
    if (this.guardrailId && this.guardrailVersion) {
      return { guardrailId: this.guardrailId, guardrailVersion: this.guardrailVersion };
    }
    const [id, version] = await Promise.all([
      SSMService.retrieveParameter(GUARDRAIL_ID_PARAM),
      SSMService.retrieveParameter(GUARDRAIL_VERSION_PARAM)
    ]);
    this.guardrailId = id;
    this.guardrailVersion = version;
    return { guardrailId: id, guardrailVersion: version };
  }

  /**
   * Apply guardrail check on content separately from model invocation.
   * This prevents false positives from PROMPT_ATTACK filters on directive prompt language.
   */
  private static async applyGuardrail(source: 'INPUT' | 'OUTPUT', textContent: string): Promise<{ allowed: boolean; message?: string }> {
    const { guardrailId, guardrailVersion } = await this.loadGuardrailConfig();
    if (!guardrailId) return { allowed: true };

    try {
      const client = this.getClient();
      const response = await client.send(
        new ApplyGuardrailCommand({
          guardrailIdentifier: guardrailId,
          guardrailVersion,
          source,
          content: [{ text: { text: textContent } }]
        })
      );

      if (response.action === 'GUARDRAIL_INTERVENED') {
        const blockedMessage = response.outputs?.[0]?.text || 'Content blocked by guardrail policy';
        logger.error('Guardrail intervened', { source, policies: interventionPolicies(response.assessments) });
        return { allowed: false, message: blockedMessage };
      }
      return { allowed: true };
    } catch (error) {
      // Name, status and requestId are enough to triage; the raw SDK error can carry the guardrail
      // ARN, the account and the region. `?? {}` — a null rejection would throw past the fail-open.
      const { name, $metadata } = (error ?? {}) as { name?: string; $metadata?: { httpStatusCode?: number; requestId?: string } };
      logger.error('Error calling ApplyGuardrail API', {
        errorName: name,
        httpStatusCode: $metadata?.httpStatusCode,
        requestId: $metadata?.requestId
      });
      // Fail open — model invocation still has standard Bedrock safety checks
      return { allowed: true };
    }
  }

  /**
   * Invoke the Bedrock foundation model using invoke_model (Anthropic Messages API).
   * Guardrail is applied separately on input/output to avoid false positives.
   *
   * @param options.maxTokens Output ceiling for this call. Generation time scales with
   *   the number of tokens produced, so callers expecting a short response should lower
   *   this to bound worst-case latency. Defaults to {@link DEFAULT_MAX_TOKENS}.
   */
  public static async invokeWithText(userMessage: string, systemPrompt?: string, options?: { maxTokens?: number }): Promise<string> {
    const client = this.getClient();

    // Step 1: Apply guardrail on user input only
    const inputCheck = await this.applyGuardrail('INPUT', userMessage);
    if (!inputCheck.allowed) {
      throw new GuardrailBlockedError('INPUT', inputCheck.message ?? 'Content blocked by guardrail policy');
    }

    // Step 2: Invoke model without guardrail params
    const requestBody = {
      anthropic_version: 'bedrock-2023-05-31',
      max_tokens: options?.maxTokens ?? DEFAULT_MAX_TOKENS,
      ...(systemPrompt && { system: systemPrompt }),
      messages: [{ role: 'user', content: [{ type: 'text', text: userMessage }] }],
      temperature: 0.2
    };

    logger.info('BedrockService: Invoking model', { modelId: MODEL_ID });

    const response = await client.send(
      new InvokeModelCommand({
        modelId: MODEL_ID,
        contentType: 'application/json',
        accept: 'application/json',
        body: JSON.stringify(requestBody)
      })
    );

    const responseBody = JSON.parse(new TextDecoder().decode(response.body));
    const responseText: string = responseBody.content?.[0]?.text || '';

    // Step 3: Apply guardrail on model output
    const outputCheck = await this.applyGuardrail('OUTPUT', responseText);
    if (!outputCheck.allowed) {
      throw new GuardrailBlockedError('OUTPUT', outputCheck.message ?? 'Content blocked by guardrail policy');
    }

    return responseText;
  }

  /**
   * Invoke the model and get a response conforming to `schema`, using the Converse
   * API's tool-use mechanism. `toolChoice: { any: {} }` forces a tool call, so the
   * model must reply with arguments matching the schema rather than free text —
   * removing the need to describe a JSON shape in the prompt and parse it back.
   *
   * Additive: `invokeWithText` is unchanged, so existing callers are unaffected.
   *
   * @throws if the guardrail blocks the input or output, or the model returns no
   *   structured payload.
   */
  public static async invokeWithSchema<T>(
    userMessage: string,
    schema: { name: string; description: string; json: JsonSchemaDocument },
    options?: { maxTokens?: number; systemPrompt?: string }
  ): Promise<T> {
    const client = this.getClient();

    const inputCheck = await this.applyGuardrail('INPUT', userMessage);
    if (!inputCheck.allowed) {
      throw new GuardrailBlockedError('INPUT', inputCheck.message ?? 'Content blocked by guardrail policy');
    }

    logger.info('BedrockService: Invoking model with schema', { modelId: MODEL_ID, tool: schema.name });

    const response = await client.send(
      new ConverseCommand({
        modelId: MODEL_ID,
        messages: [{ role: 'user', content: [{ text: userMessage }] }],
        ...(options?.systemPrompt ? { system: [{ text: options.systemPrompt }] } : {}),
        inferenceConfig: { maxTokens: options?.maxTokens ?? DEFAULT_MAX_TOKENS, temperature: 0.2 },
        toolConfig: {
          tools: [{ toolSpec: { name: schema.name, description: schema.description, inputSchema: { json: schema.json } } }],
          toolChoice: { any: {} }
        }
      })
    );

    const toolUse = response.output?.message?.content?.find((block) => block.toolUse)?.toolUse;
    if (!toolUse?.input) {
      throw new Error('Model returned no structured output');
    }

    // Guardrail the rendered payload, matching invokeWithText's output check.
    const outputCheck = await this.applyGuardrail('OUTPUT', JSON.stringify(toolUse.input));
    if (!outputCheck.allowed) {
      throw new GuardrailBlockedError('OUTPUT', outputCheck.message ?? 'Content blocked by guardrail policy');
    }

    return toolUse.input as T;
  }
}
